/**
 * Equipo del estudio — miembros del estudio contable (firm_admin).
 *
 * Lista el staff (FirmMembership) y permite agregar usuarios al estudio con su
 * rol (firm_admin / firm_staff). Es ORTOGONAL al RBAC por-tenant: estar en el
 * equipo gobierna el acceso al portal; el acceso a cada empresa se gestiona con
 * la provisión masiva.
 */
import { DataTable } from '@/components/ds/DataTable'
import { useState } from 'react'
import { Users, UserPlus } from 'lucide-react'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { Badge, type BadgeConfig, ConfigBadge } from '@/components/Badge'
import { Button } from '@/components/Button'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import { ToastContainer, useToast } from '@/components/Toast'
import { getApiErrorMessage } from '@/lib/apiClient'
import {
  useFirmStaff, useAddStaff, useFirmStaffCandidates, type FirmRole,
} from '@/modules/firm/hooks/useFirm'

const fieldCls =
  'w-full px-3 py-2 text-sm rounded-lg bg-surface border border-divider text-content focus:outline-none focus:ring-2 focus:ring-focus-ring'

const ROLE_CFG: BadgeConfig<FirmRole> = {
  firm_admin: { label: 'Administrador', variant: 'indigo' },
  firm_staff: { label: 'Contador', variant: 'blue' },
}

const STATUS_CFG: BadgeConfig<'pending' | 'accepted' | 'revoked'> = {
  accepted: { label: 'Activo', variant: 'emerald' },
  pending: { label: 'Pendiente', variant: 'amber' },
  revoked: { label: 'Revocado', variant: 'slate' },
}

export function FirmStaffPage() {
  const { data: staff, isLoading } = useFirmStaff()
  const { data: candidatesData } = useFirmStaffCandidates()
  const addStaff = useAddStaff()
  const toast = useToast()

  const [open, setOpen] = useState(false)
  const [userId, setUserId] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<FirmRole>('firm_staff')

  // Candidatos = usuarios de TU empresa (tenant propio), ya filtrados en el backend.
  const candidates = candidatesData?.users ?? []
  const ownTenant = candidatesData?.own_tenant ?? null

  const reset = () => { setUserId(''); setEmail(''); setRole('firm_staff') }

  const submit = async () => {
    const payload = email.trim()
      ? { email: email.trim(), firm_role: role }
      : userId
        ? { user_id: userId, firm_role: role }
        : null
    if (!payload) return
    try {
      await addStaff.mutateAsync(payload)
      toast.success('Miembro agregado al equipo.')
      setOpen(false)
      reset()
    } catch (e) {
      toast.error(getApiErrorMessage(e))
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <PageHeader
        title="Equipo del estudio"
        subtitle="Contadores y administradores con acceso al portal del estudio."
        help={HELP['firm.staff']}
      >
        <Button onClick={() => setOpen(true)} variant="primary">
          <UserPlus size={15} /> Agregar al equipo
        </Button>
      </PageHeader>

      <div className="bg-glass backdrop-blur-glass border border-divider rounded-card overflow-hidden shadow-card">
        {isLoading ? (
          <TableSkeleton rows={4} cols={3} />
        ) : (staff ?? []).length === 0 ? (
          <EmptyState icon={Users} title="Sin miembros" hint="Agrega contadores al equipo del estudio." />
        ) : (
          <DataTable
            columns={[
              {
                key: 'miembro', header: 'Miembro', sortable: true,
                sortValue: m => m.user_detail.full_name || m.user_detail.email,
                render: m => (
                  <>
                    <span className="font-medium text-content">{m.user_detail.full_name || '—'}</span>
                    <span className="block text-xs text-content-subtle">{m.user_detail.email}</span>
                  </>
                ),
              },
              { key: 'rol', header: 'Rol', sortable: true, sortValue: m => m.firm_role, render: m => <ConfigBadge value={m.firm_role} config={ROLE_CFG} /> },
              { key: 'estado', header: 'Estado', sortable: true, sortValue: m => m.status, render: m => <ConfigBadge value={m.status} config={STATUS_CFG} /> },
            ]}
            rows={staff ?? []}
            caption="Miembros del estudio"
          />
        )}
      </div>

      <Modal
        open={open}
        onClose={() => { setOpen(false); reset() }}
        title="Agregar miembro al equipo"
        size="sm"
        actions={
          <>
            <ModalCancelButton onClick={() => { setOpen(false); reset() }} />
            <Button onClick={submit} disabled={!userId && !email.trim()} isPending={addStaff.isPending} variant="primary" size="lg">
              Agregar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-content mb-1">
              Usuario de tu empresa{ownTenant ? ` · ${ownTenant.tenant_name}` : ''}
            </label>
            <select aria-label="Seleccionar un usuario"
              value={userId}
              onChange={(e) => { setUserId(e.target.value); if (e.target.value) setEmail('') }}
              disabled={!!email.trim()}
              className={fieldCls}
            >
              <option value="">Seleccionar un usuario…</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name || u.email} · {u.email}</option>
              ))}
            </select>
            {!ownTenant && (
              <p className="text-xs text-content-subtle mt-1">
                Tu estudio no tiene una empresa propia marcada. Márcala en <strong>Empresas</strong> para
                listar tu equipo, o engánchalo por correo abajo.
              </p>
            )}
            {ownTenant && candidates.length === 0 && (
              <p className="text-xs text-content-subtle mt-1">
                No hay usuarios nuevos en tu empresa para agregar.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="firmstaffpage-o-enganchalo-por-correo" className="block text-sm font-medium text-content mb-1">…o engánchalo por correo</label>
            <input id="firmstaffpage-o-enganchalo-por-correo"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (e.target.value) setUserId('') }}
              placeholder="correo@dominio.cl"
              className={fieldCls}
            />
            <p className="text-xs text-content-subtle mt-1">
              El usuario debe tener cuenta en Clobi (por ahora no se crean cuentas nuevas desde aquí).
            </p>
          </div>

          <div>
            <label htmlFor="firmstaffpage-rol-en-el-estudio" className="block text-sm font-medium text-content mb-1">Rol en el estudio</label>
            <select id="firmstaffpage-rol-en-el-estudio" value={role} onChange={(e) => setRole(e.target.value as FirmRole)} className={fieldCls}>
              <option value="firm_staff">Contador (ve su cartera asignada)</option>
              <option value="firm_admin">Administrador (gestiona el estudio)</option>
            </select>
          </div>

          <p className="text-xs text-content-subtle">
            <Badge label="Nota" variant="slate" /> Agregar al equipo <strong>no</strong> da acceso a las empresas
            ni consume licencia. El acceso (y la licencia) se otorga por empresa con la provisión masiva desde{' '}
            <strong>Empresas</strong>.
          </p>
        </div>
      </Modal>

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}

export default FirmStaffPage
